<template>
  <div class="py-3 overview-container">
    <TimelineRoot>
      <TimelineItem
        v-for="(item, index) in orderedTimelineItems"
        :key="item.id"
        :title="$t(item.titleKey)"
        :actions="item.actions"
      >
        <template #icon>{{ index + 1 }}</template>
        <template #content>
          <component :is="item.component" v-bind="item.props" />
        </template>
      </TimelineItem>
    </TimelineRoot>
  </div>
  <LoadingOverlayComponent v-if="isLoading" />
</template>

<script setup lang="ts">
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import ProjectedProductionOverview, {
  type ProjectedProductionOverviewProps,
} from "@/components/ProjectedProductionOverview.vue";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { useProjections } from "@/composables/useProjections";
import { useQueryManager } from "@/composables/useQueryManager";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import { Company, RegistryDevice } from "@/types";
import type { Component } from "vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ForwardSalesTabComponent from "./forwards/ForwardSalesTabComponent.vue";
import PPAStrategyOverview, {
  type PPAStrategyOverviewProps,
} from "./PPA/PPAStrategyOverview.vue";
import HoldStrategyOverview, {
  type HoldStrategyOverviewProps,
} from "./spot/HoldStrategyOverview.vue";
import SpotSalesStrategyOverview, {
  type SpotSalesStrategyOverviewProps,
} from "./spot/SpotSalesStrategyOverview.vue";
import TimelineItem from "./TimelineItem.vue";
import TimelineRoot from "./TimelineRoot.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface ActionButton {
  label: string;
  icon?: string;
  variant?: string;
  onClick: () => void;
}

// Base structure for any timeline item
interface BaseTimelineItem {
  id: string;
  titleKey: string;
  component: Component;
  actions?: ActionButton[];
}

// Specific timeline item types using a discriminated union
export interface ProjectedProductionTimelineItem extends BaseTimelineItem {
  component: typeof ProjectedProductionOverview;
  props: ProjectedProductionOverviewProps;
}

export interface ForwardSalesTimelineItem extends BaseTimelineItem {
  component: typeof ForwardSalesTabComponent;
  props: { company: Company };
}

export interface PPATimelineItem extends BaseTimelineItem {
  component: typeof PPAStrategyOverview;
  props: PPAStrategyOverviewProps;
}

export interface HoldTimelineItem extends BaseTimelineItem {
  component: typeof HoldStrategyOverview;
  props: HoldStrategyOverviewProps;
}

export interface SpotSalesTimelineItem extends BaseTimelineItem {
  component: typeof SpotSalesStrategyOverview;
  props: SpotSalesStrategyOverviewProps;
}

// A union type for any possible timeline item
export type TimelineItemData =
  | ProjectedProductionTimelineItem
  | ForwardSalesTimelineItem
  | PPATimelineItem
  | HoldTimelineItem
  | SpotSalesTimelineItem;

const props = defineProps<{
  company: Company;
  registryDevices: RegistryDevice[];
}>();

const isLoading = ref(false);
const productionStore = useProductionStrategyStore();

const { globalErrorHandler } = useGlobalHandlers();
const { updateQuery } = useQueryManager();
const { t } = useI18n();

const { projections, fetchProjections } = useProjections({
  onError: globalErrorHandler,
});

const { hasFlag } = useFeatureFlags(props.company);

// Feature flag checks
const showPPAStrategy = hasFlag("ppa_allocation");
const showHoldStrategy = hasFlag("hold_allocation");

// Centralized edit handlers
const editHandlers = {
  ppa: () =>
    updateQuery({
      "trading-strategy-active-tab": "strategy-customisation",
      "strategy-active-tab": "ppa",
    }),
  hold: () =>
    updateQuery({
      "trading-strategy-active-tab": "strategy-customisation",
      "strategy-active-tab": "hold",
    }),
  spot: () =>
    updateQuery({
      "trading-strategy-active-tab": "strategy-customisation",
      "strategy-active-tab": "spot",
    }),
};

// Action configuration helper
const createEditAction = (handler: () => void): ActionButton[] => [
  {
    label: t("common.edit"),
    icon: "pencil-alt",
    variant: "btn-sm btn-subtle-primary ms-2 py-1 px-2 fs-9",
    onClick: handler,
  },
];

const orderedTimelineItems = computed((): TimelineItemData[] => {
  const items: TimelineItemData[] = [];

  // 1. Projected Production
  items.push({
    id: "projectedProduction",
    titleKey: "tradingCenter.overview.projectedProduction.title",
    component: ProjectedProductionOverview,
    props: {
      projections: projections.value,
    },
  });

  // 2. PPA Strategy (only if feature flag is enabled)
  if (showPPAStrategy.value) {
    items.push({
      id: "ppa",
      titleKey: "tradingCenter.customization.ppaReserve",
      component: PPAStrategyOverview,
      props: {
        portfolios: JSON.parse(JSON.stringify(productionStore.portfolios)),
        registryDevices: props.registryDevices,
      },
      actions: createEditAction(editHandlers.ppa),
    });
  }

  // 3. Forward Sales
  if (props.company.forward_sales_enabled) {
    items.push({
      id: "forwardSales",
      titleKey: "tradingCenter.customization.forwardSales",
      component: ForwardSalesTabComponent,
      props: { company: props.company },
    });
  }

  // 4. Hold Strategy (only if feature flag is enabled)
  if (showHoldStrategy.value) {
    items.push({
      id: "hold",
      titleKey: "tradingCenter.customization.holdPortfolio",
      component: HoldStrategyOverview,
      props: {
        portfolios: JSON.parse(JSON.stringify(productionStore.portfolios)),
        registryDevices: props.registryDevices,
      },
      actions: createEditAction(editHandlers.hold),
    });
  }

  // 5. Spot Sales
  items.push({
    id: "spotSales",
    titleKey: "tradingCenter.overview.spot.title",
    component: SpotSalesStrategyOverview,
    props: {
      company: props.company,
    },
    actions: createEditAction(editHandlers.spot),
  });

  return items;
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await fetchProjections(props.company.id);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
.overview-container {
  max-width: 800px;
}
</style>
